import { Controller } from "@hotwired/stimulus"

// Delivery form: parse a pi-ledger session JSONL into itemized line items,
// let operators split or relabel the recorded hours, and keep the hidden JSON
// preview synchronized. The server reparses and reconciles the ledger at send time.
export default class extends Controller {
  static targets = [
    "ledgerFile",
    "evidenceFiles",
    "lineItemsJson",
    "lineItemsBody",
    "sessionId",
    "project",
    "author",
    "generatedAt",
    "agentHours",
    "humanHours",
    "agentCost",
    "humanCost",
    "totalCost",
    "confirmationPhrase",
    "confirmationText",
    "parseStatus"
  ]

  static values = {
    parseUrl: String,
    ticketId: String,
    agentRate: Number,
    humanRate: Number
  }

  connect() {
    if (this.hasLineItemsBodyTarget) {
      const existing = this.lineItemsBodyTarget.querySelectorAll("tr.delivery-line")
      if (existing.length === 0) {
        this.appendRow({ kind: "agent" })
      } else {
        existing.forEach((row) => this.bindRow(row))
      }
    }
    this.sync()
  }

  async parse() {
    const file = this.ledgerFileTarget.files?.[0]
    if (!file) return

    try {
      await this.parseFile(file)
    } catch (err) {
      this.setParseStatus(`Failed to parse ledger session: ${err.message}`)
    }
  }

  async parseEvidence() {
    const files = Array.from(this.evidenceFilesTarget.files || [])
      .filter((file) => file.name.toLowerCase().endsWith(".jsonl"))
    if (files.length === 0) return

    for (const file of files) {
      try {
        await this.parseFile(file)
        return
      } catch {
        // Evidence can contain unrelated JSONL files; try the next candidate.
      }
    }
    this.setParseStatus("No billable pi-ledger entries found in the uploaded JSONL evidence.")
  }

  async parseFile(file) {
    this.setParseStatus(`Parsing ${file.name}…`)
    const form = new FormData()
    form.append("ledger_session", file)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(this.parseUrlValue, {
      method: "POST",
      headers: token ? { "X-CSRF-Token": token } : {},
      body: form
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    if (!Array.isArray(data.line_items) || data.line_items.length === 0) {
      throw new Error("no billable entries found")
    }
    this.applyParsed(data)
    this.setParseStatus(`Auto-filled from ${file.name}. Review before sending.`)
  }

  applyParsed(data) {
    if (data.session_id != null && this.hasSessionIdTarget) this.sessionIdTarget.value = data.session_id
    if (data.project != null && this.hasProjectTarget) this.projectTarget.value = data.project
    if (data.author != null && this.hasAuthorTarget) this.authorTarget.value = data.author
    if (data.generated_at != null && this.hasGeneratedAtTarget) this.generatedAtTarget.value = data.generated_at
    this.renderLineItems(Array.isArray(data.line_items) ? data.line_items : [])
  }

  renderLineItems(items) {
    if (!this.hasLineItemsBodyTarget) return
    this.lineItemsBodyTarget.innerHTML = ""
    items.forEach((item) => this.appendRow(item))
    this.sync()
  }

  addRow(event) {
    event?.preventDefault()
    this.appendRow({
      kind: "agent",
      label: "",
      hours: 0,
      rate_per_hour: this.agentRateValue,
      cost: 0
    })
  }

  appendRow(item) {
    const kind = item.kind === "human" ? "human" : "agent"
    const hours = Number(item.hours || 0)
    const rate = Number(item.rate_per_hour || 0)
    const row = document.createElement("tr")
    row.className = "delivery-line"
    row.innerHTML = this.rowHtml(kind, item.label || "", hours, rate)
    this.lineItemsBodyTarget.appendChild(row)
    this.bindRow(row)
    this.sync()
  }

  bindRow(row) {
    const kindSelect = row.querySelector("select")
    kindSelect?.addEventListener("change", () => {
      const rateInput = row.querySelector("[data-line-item-rate]")
      if (rateInput) {
        rateInput.value = kindSelect.value === "human" ? this.humanRateValue : this.agentRateValue
      }
      this.sync()
    })
    row.querySelectorAll("input").forEach((el) => {
      el.addEventListener("input", () => this.sync())
      el.addEventListener("change", () => this.sync())
    })
    row.querySelector("[data-action$='removeRow']")?.addEventListener("click", (e) => {
      e.preventDefault()
      row.remove()
      this.sync()
    })
  }

  rowHtml(kind, label, hours, rate) {
    return `
      <td><select aria-label="Line item kind" class="w-full border border-gray-300 rounded px-2 py-1 text-sm">
        <option value="agent"${kind === "agent" ? " selected" : ""}>Agent</option>
        <option value="human"${kind === "human" ? " selected" : ""}>Human</option>
      </select></td>
      <td><input type="text" value="${this.escape(label)}" placeholder="Line item label" aria-label="Line item label"
        class="w-full border border-gray-300 rounded px-2 py-1 text-sm" /></td>
      <td><input type="number" step="0.0001" min="0" value="${hours}" aria-label="Line item hours"
        class="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right" /></td>
      <td><input type="number" readonly step="0.01" min="0" value="${rate}" aria-label="Line item rate" data-line-item-rate
        class="w-24 border border-gray-300 rounded bg-gray-50 px-2 py-1 text-sm text-right" /></td>
      <td class="cost text-right text-sm text-gray-700">$0.00</td>
      <td class="text-right"><button type="button" data-action="click->delivery-form#removeRow"
        class="text-error-600 hover:text-error-700 text-sm font-medium">Remove</button></td>
    `
  }

  sync() {
    const items = []
    let agentHours = 0
    let humanHours = 0
    let agentCost = 0
    let humanCost = 0

    this.lineItemsBodyTarget.querySelectorAll("tr.delivery-line").forEach((row) => {
      const cells = row.querySelectorAll("input, select")
      const kind = cells[0].value
      const label = cells[1].value
      const hours = this.num(cells[2].value)
      const rate = this.num(cells[3].value)
      const cost = hours * rate
      row.querySelector(".cost").textContent = `$${cost.toFixed(2)}`
      items.push({ kind, label, hours, rate_per_hour: rate, cost: Number(cost.toFixed(2)) })
      if (kind === "agent") {
        agentHours += hours
        agentCost += cost
      } else {
        humanHours += hours
        humanCost += cost
      }
    })

    if (this.hasLineItemsJsonTarget) this.lineItemsJsonTarget.value = JSON.stringify(items)
    if (this.hasAgentHoursTarget) this.agentHoursTarget.textContent = agentHours.toFixed(4)
    if (this.hasHumanHoursTarget) this.humanHoursTarget.textContent = humanHours.toFixed(4)
    if (this.hasAgentCostTarget) this.agentCostTarget.textContent = `$${agentCost.toFixed(2)}`
    if (this.hasHumanCostTarget) this.humanCostTarget.textContent = `$${humanCost.toFixed(2)}`
    const total = agentCost + humanCost
    if (this.hasTotalCostTarget) this.totalCostTarget.textContent = `$${total.toFixed(2)}`
    if (this.hasConfirmationPhraseTarget) {
      this.confirmationPhraseTarget.textContent = `SEND ${this.ticketIdValue} $${total.toFixed(2)}`
    }
  }

  setParseStatus(msg) {
    if (this.hasParseStatusTarget) this.parseStatusTarget.textContent = msg
  }

  num(v) { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

  escape(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
